import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ENVS, FALLBACK_ERROR_MESSAGE, REGIONS } from "@/lib/constants";
import { createSampleDeploy, getSampleApplications } from "@/lib/deploy/api";
import { PATH } from "@/lib/enums";
import { humanizeString } from "@/lib/utils";
import { SampleApplication } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Github, Loader2, Rocket } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface OnboardingFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export function OnboardingFlow({
  open,
  onOpenChange,
  onComplete,
}: OnboardingFlowProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const defaultRegion = REGIONS[0] ?? "us-east-1";
  const defaultEnv = ENVS[0] ?? "production";

  const [selectedOption, setSelectedOption] = useState<
    "github" | "sample" | null
  >(null);
  const [selectedSampleSlug, setSelectedSampleSlug] = useState<
    string | undefined
  >(undefined);
  const [selectedRegion, setSelectedRegion] = useState<string>(defaultRegion);
  const [selectedEnv, setSelectedEnv] = useState<string>(defaultEnv);

  const resetSelections = () => {
    setSelectedOption(null);
    setSelectedSampleSlug(undefined);
    setSelectedRegion(defaultRegion);
    setSelectedEnv(defaultEnv);
  };

  const handleDialogChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetSelections();
    }
    onOpenChange(nextOpen);
  };

  useEffect(() => {
    if (!open) {
      resetSelections();
    }
  }, [open, defaultEnv, defaultRegion]);

  const sampleAppsQuery = useQuery({
    queryKey: ["sample-applications"],
    queryFn: async () => {
      const res = await getSampleApplications();
      return res.data as SampleApplication[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: open && selectedOption === "sample",
  });

  const sampleDeployMutation = useMutation({
    mutationFn: createSampleDeploy,
    onSuccess: (res) => {
      const deploy = res.data;
      const deployId = deploy?.id ?? deploy?.data?.id;
      toast({
        title: "Sample deploy queued",
        description: "We are spinning up your sample application now.",
      });
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      resetSelections();
      onComplete();
      onOpenChange(false);
      if (deployId) {
        navigate(`${PATH.APPLICATIONS}/${deployId}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to deploy sample",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });

  const samples = sampleAppsQuery.data ?? [];
  const isLoadingSamples =
    selectedOption === "sample" &&
    sampleAppsQuery.isFetching &&
    !sampleAppsQuery.data?.length;
  const sampleError = sampleAppsQuery.error as any;

  const handleConnectGithub = () => {
    resetSelections();
    onOpenChange(false);
    navigate(PATH.GITHUB);
  };

  const handleSelectGithubOption = () => {
    setSelectedOption("github");
  };

  const handleSelectSampleOption = () => {
    setSelectedOption("sample");
    setSelectedSampleSlug(undefined);
    setSelectedRegion(defaultRegion);
    setSelectedEnv(defaultEnv);
  };

  const handleSampleDeploy = async () => {
    if (!selectedSampleSlug) {
      toast({
        title: "Select a sample app",
        description: "Choose a sample application to deploy.",
        variant: "destructive",
      });
      return;
    }

    await sampleDeployMutation.mutateAsync({
      sampleSlug: selectedSampleSlug,
      region: selectedRegion,
      env: selectedEnv,
    });
  };

  const handleDismiss = () => {
    resetSelections();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-4xl h-[90vh] bg-slate-900 border-cyan-500/30 overflow-hidden">
        <DialogHeader className="border-b border-slate-700/50 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-3 text-cyan-400 text-xl">
                <Rocket className="h-6 w-6" />
                Launch your first deploy
              </DialogTitle>
              <DialogDescription className="text-slate-400 mt-1">
                Pick how you want to get started. You can connect your own code
                or explore a sample application.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-6">
          {selectedOption !== "sample" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card
                className={
                  selectedOption === "github"
                    ? "holo-card border-blue-400 ring-1 ring-blue-400/40 transition-all cursor-pointer group"
                    : "holo-card border-blue-500/50 hover:border-blue-400 transition-all cursor-pointer group"
                }
                onClick={handleSelectGithubOption}
              >
                <CardContent className="p-6 text-center">
                  <Github className="h-12 w-12 text-blue-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold text-blue-100 mb-2">
                    Connect GitHub repository
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Link your GitHub repo for automated builds on every push.
                  </p>
                  <Badge className="text-blue-400 bg-blue-400/10 border-blue-400/30">
                    RECOMMENDED
                  </Badge>
                </CardContent>
              </Card>

              <Card
                className={
                  //@ts-ignore
                  selectedOption === "sample"
                    ? "holo-card border-cyan-400 ring-1 ring-cyan-400/40 transition-all cursor-pointer group"
                    : "holo-card border-purple-500/50 hover:border-purple-400 transition-all cursor-pointer group"
                }
                onClick={handleSelectSampleOption}
              >
                <CardContent className="p-6 text-center">
                  <Rocket className="h-12 w-12 text-purple-400 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold text-purple-100 mb-2">
                    Deploy a sample application
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Explore the platform with curated apps that deploy in
                    seconds.
                  </p>
                  <Badge className="text-purple-400 bg-purple-400/10 border-purple-400/30">
                    QUICK START
                  </Badge>
                </CardContent>
              </Card>
            </div>
          )}

          {selectedOption === "github" && (
            <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-4 text-sm text-blue-50">
              <p className="font-semibold text-blue-100">
                Connect your repository
              </p>
              <p className="mt-1 text-blue-50/80">
                We'll guide you through selecting a GitHub repository and
                configuring your first deploy pipeline.
              </p>
            </div>
          )}

          {selectedOption === "sample" && (
            <div className="space-y-5">
              {isLoadingSamples && (
                <div className="flex items-center justify-center gap-2 rounded-md border border-slate-700/60 bg-slate-900/70 p-4 text-cyan-100">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Loading sample applications...
                </div>
              )}

              {sampleError && !isLoadingSamples && (
                <div className="rounded-md border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
                  Failed to load sample applications.{" "}
                  {sampleError?.response?.data?.message ?? sampleError?.message}
                </div>
              )}

              {!isLoadingSamples && !sampleError && samples.length === 0 && (
                <div className="rounded-md border border-slate-700/60 bg-slate-900/70 p-4 text-sm text-slate-300">
                  No sample applications are available yet. Check back soon!
                </div>
              )}

              {!isLoadingSamples && !sampleError && samples.length > 0 && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-slate-300">Sample application</Label>
                    <Select
                      value={selectedSampleSlug}
                      onValueChange={setSelectedSampleSlug}
                    >
                      <SelectTrigger className="bg-slate-800/50 border-cyan-500/30 text-cyan-100">
                        <SelectValue placeholder="Select a sample application" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-cyan-500/30 text-cyan-100">
                        {samples.map((sample) => (
                          <SelectItem key={sample.slug} value={sample.slug}>
                            {sample.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Region</Label>
                    <Select
                      value={selectedRegion}
                      onValueChange={setSelectedRegion}
                    >
                      <SelectTrigger className="bg-slate-800/50 border-cyan-500/30 text-cyan-100">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-cyan-500/30 text-cyan-100">
                        {REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>
                            {humanizeString(region)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Environment</Label>
                    <Select value={selectedEnv} onValueChange={setSelectedEnv}>
                      <SelectTrigger className="bg-slate-800/50 border-cyan-500/30 text-cyan-100">
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-cyan-500/30 text-cyan-100">
                        {ENVS.map((environment) => (
                          <SelectItem key={environment} value={environment}>
                            {humanizeString(environment)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between  border-t border-slate-700/50">
          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-200"
            onClick={handleDismiss}
            disabled={sampleDeployMutation.isPending}
          >
            Not now
          </Button>

          <div className="flex gap-2">
            {selectedOption && (
              <Button
                variant="outline"
                className="cyber-button"
                onClick={() => setSelectedOption(null)}
                disabled={sampleDeployMutation.isPending}
              >
                Back
              </Button>
            )}

            {selectedOption === "github" && (
              <Button className="cyber-button" onClick={handleConnectGithub}>
                <Github className="mr-2 h-4 w-4" />
                Connect GitHub
              </Button>
            )}

            {selectedOption === "sample" && (
              <Button
                className="cyber-button"
                onClick={handleSampleDeploy}
                disabled={!selectedSampleSlug || sampleDeployMutation.isPending}
              >
                {sampleDeployMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Deploy sample app
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
